import { create } from 'zustand';
// Imported from the defining module rather than the feature barrel: this store
// reads the constant at MODULE-INIT time (inside create()), and the barrel
// re-exports this file, so going through it is a cycle that lands in the
// temporal dead zone — "Cannot access 'SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS'
// before initialization". Components can use the barrel safely because they
// read at render time, long after every module has initialized.
import {
  SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
  type ShortTermCareInflationOption,
} from '@/features/shop-insurance/hooks/use-short-term-care-quotes';

interface ShortTermCareFiltersState {
  /** Multi-select; every amount is checked by default. */
  selectedDailyBenefitAmounts: number[];
  /** Monthly ceiling; null means no premium limit. */
  maxMonthlyPremium: number | null;
  inflationOption: ShortTermCareInflationOption;
  /**
   * Highest premium among the quotes currently fetched, so the ceiling options
   * can be built from what this client is actually offered. Hardcoded ceilings
   * were calibrated to one profile and silently excluded real quotes above
   * them — premium scales hard with issue age, and the inflation rider adds
   * ~40% on top.
   */
  observedMaxMonthlyPremium: number | null;
  actions: {
    toggleDailyBenefitAmount: (amount: number) => void;
    setAllDailyBenefitAmounts: (selected: boolean) => void;
    setMaxMonthlyPremium: (value: number | null) => void;
    setInflationOption: (value: ShortTermCareInflationOption) => void;
    setObservedMaxMonthlyPremium: (value: number | null) => void;
  };
}

/**
 * Short-term care filter state.
 *
 * A store rather than component state because the controls render in the shared
 * filters sidebar while the quotes render in ShortTermCareShopView — two
 * subtrees with no common ancestor short of ShopInsuranceContent. Threading
 * props through that would mean adding STC state to shared shop code, which is
 * what the isolated view exists to avoid. Matches the pattern the sibling
 * filters already use (carrier exclusions, benefit type, eligibility).
 */
export const useShortTermCareFiltersStore = create<ShortTermCareFiltersState>(set => ({
  selectedDailyBenefitAmounts: SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS,
  maxMonthlyPremium: null,
  inflationOption: 'none',
  observedMaxMonthlyPremium: null,
  actions: {
    toggleDailyBenefitAmount: amount =>
      set(state => ({
        // Kept ascending so re-checking the same set reproduces the identical
        // query key and hits cache instead of refetching.
        selectedDailyBenefitAmounts: state.selectedDailyBenefitAmounts.includes(amount)
          ? state.selectedDailyBenefitAmounts.filter(value => value !== amount)
          : [...state.selectedDailyBenefitAmounts, amount].sort((a, b) => a - b),
      })),
    setAllDailyBenefitAmounts: selected =>
      set({ selectedDailyBenefitAmounts: selected ? SHORT_TERM_CARE_DAILY_BENEFIT_AMOUNTS : [] }),
    setMaxMonthlyPremium: value => set({ maxMonthlyPremium: value }),
    setInflationOption: value => set({ inflationOption: value }),
    setObservedMaxMonthlyPremium: value => set({ observedMaxMonthlyPremium: value }),
  },
}));

// Atomic selectors rather than one object selector: zustand v4 compares by
// reference, so returning a fresh object would re-render every consumer on any
// store change. Same shape as the sibling filter stores.
export const useSelectedDailyBenefitAmounts = () =>
  useShortTermCareFiltersStore(state => state.selectedDailyBenefitAmounts);

export const useMaxMonthlyPremium = () =>
  useShortTermCareFiltersStore(state => state.maxMonthlyPremium);

export const useShortTermCareInflationOption = () =>
  useShortTermCareFiltersStore(state => state.inflationOption);

export const useObservedMaxMonthlyPremium = () =>
  useShortTermCareFiltersStore(state => state.observedMaxMonthlyPremium);

export const useShortTermCareFiltersActions = () =>
  useShortTermCareFiltersStore(state => state.actions);
